import api from '@/api';
import Logger from '@/logger';
import { NoActiveClassification, ProductDoesNotExists, TaggedFeatureError } from '../exceptions';

const SELLER_ID = process.env.VUE_APP_NER_CLASSIFICATIONS_SELLER_ID
const DATE = process.env.VUE_APP_NER_CLASSIFICATIONS_DATE

if (!SELLER_ID) throw new Error('Please provide the VUE_APP_NER_CLASSIFICATIONS_SELLER_ID env variable')
if (!DATE) throw new Error('Please provide the VUE_APP_NER_CLASSIFICATIONS_DATE env variable')

const logger = new Logger('store/ner/actions')

/**
 * Create a tagged feature and add a feature to the product.
 * 
 * The action only needs the feature's id because the offset, value and product
 * are in the selection object in the state.
 * 
 * @param {Object} context
 * @param {String} featureId the feature's id
 */
async function addTaggedFeatureToSelectedProduct({ dispatch, state }, featureId) {
  const { dataId, offset, product, value } = state.selection

  /* Validate that the product does not have already that feature */
  const index = state.products.findIndex(({ id, sellerId }) => id === product.id && sellerId === product.sellerId)
  if (index === -1) {
    throw new ProductDoesNotExists()
  }
  if (state.products[index].taggedFeatures.some(f => f.featureId === featureId)) {
    throw new TaggedFeatureError(`The product already has the feature with id "${featureId}"`)
  }

  /* If it's all ok, perform the action */
  const taggedFeature = { dataId, featureId, offset, value }
  return dispatch('saveTaggedFeature', { taggedFeature, product })
}

/**
 * Create a new feature for the current activate classification.
 * @param {Object} context
 * @param {String} name The name to set to the new feature.
 */
function createFeature({ commit, state }, name) {
  if (!state.classification) throw new NoActiveClassification()

  const classification = state.classification.id
  commit('setLoadingCreateFeature', true)
  return api.features.create({ name, classification }).then(feature => {
    commit('addFeature', feature)
    commit('setLoadingCreateFeature', false)
  }).catch(err => {
    logger.error(err)
    commit('setLoadingCreateFeature', false)
    throw err
  })
}

/**
 * Get and set the classifications.
 * @param {Object} context
 */
function getClassifications({ commit }) {
  commit('setLoadingClassifications', true)
  return api.classifications.get({ seller: SELLER_ID, date: DATE }).then(classifications => {
    commit('setClassifications', classifications)
    commit('setLoadingClassifications', false)
  }).catch(err => {
    logger.error(err)
    commit('setLoadingClassifications', false)
    throw err
  })
}

/**
 * Get all the features of the current active classification and then get the products.
 * @param {Object} context
 */
function getFeatures({ commit, dispatch, state }) {
  if (!state.classification) throw new NoActiveClassification()

  commit('setLoadingFeatures', true)
  return api.features.get(state.classification.id).then(features => {
    features = features.map(f => ({ ...f, onlyMissing: true }))
    commit('setFeatures', features)
    commit('setLoadingFeatures', false)
    commit('setProductsPage', 1)
    dispatch('getProducts')
  }).catch(err => {
    logger.error(err)
    commit('setLoadingFeatures', false)
    throw err;
  })
}

/**
 * Get the products of the current active classification.
 * 
 * If no page is provided it will try to get the page that is in the store
 * and set the products array.
 * 
 * It will load the items already tagged for the features that have the the property
 * `getTaggedProducts` as `true`.
 * 
 * @param {Object} context
 */
function getProducts({ commit, state }) {
  if (!state.classification) throw new NoActiveClassification()

  if (!state.features.length) throw new Error('FOO')

  const page = state.productsPage
  const classificationId = state.classification.id
  const avoidFeatureIds = state.features.filter(f => f.onlyMissing).map(f => f.id)
  commit('setLoadingProducts', true)
  commit('setProducts', [])
  return api.matchingItemsData.get({ avoidFeatureIds, classificationId, page }).then(({ pages, products }) => {
    commit('setProductsPage', page)
    commit('setProductsPagesLength', pages)
    commit('setProducts', products)
    commit('setLoadingProducts', false)
  }).catch(err => {
    logger.error(err)
    commit('setLoadingProducts', false)
    throw err
  })
}

/**
 * Remove a feature from the database and from the store.
 * @param {Object} context
 * @param {String} name The name of the feature to delete.
 */
function removeFeature({ commit, state }, id) {
  if (!state.classification) throw new NoActiveClassification()

  commit('setRemovingFeature', id)
  return api.features.remove({ id })
    .then(() => {
      commit('removeFeature', id)
    }).catch(err => {
      logger.error(err)
      throw err
    })
}

/**
 * Remove the tagged feature from the database and from the product in the store.
 * 
 * @param {Object} context
 * @param {Object} payload
 * @param {Object} payload.product the product object with id and sellerId
 * @param {Object} payload.taggedFeature the entire Tagged Feature object
 */
function removeTaggedFeatureFromProduct({ commit }, { product, taggedFeature }) {
  commit('setLoadingRemoveTaggedFeature', true)
  return api.taggedFeatures.remove(taggedFeature.id).then(() => {
    commit('removeTaggedFeatureFromProduct', { product, taggedFeature })
    commit('setLoadingRemoveTaggedFeature', false)
  }).catch(err => {
    logger.error(err)
  })
}

/**
 * Save a tagged feature and add it to the given product.
 *
 * @param {Object} context
 * @param {Object} payload
 * @param {Object} taggedFeature with {dataId, featureId, offset, value}
 * @param {Object} product with {id, sellerId}
 */
function saveTaggedFeature({ commit }, { taggedFeature, product }) {
  commit('setLoadingAddTaggedFeatureToProduct', true)
  return api.taggedFeatures.create(taggedFeature).then(taggedFeature => {
    commit('addTaggedFeatureToProduct', { product, taggedFeature })
    commit('setLoadingAddTaggedFeatureToProduct', false)
  }).catch(err => {
    logger.error(err)
    commit('setLoadingAddTaggedFeatureToProduct', false)
    throw err
  })
}

/**
 * Set the `onlyMissing` property of a feature identified by its id and dispatch again
 * the `getProducts` action.
 * @param {Object} context
 * @param {Object} payload
 * @param {Number} payload.id the id of the feature to set its property
 * @param {Boolean} payload.value the value to set.
 */
function setOnlyMissing({ commit, dispatch }, { id, value }) {
  commit('setOnlyMissing', { id, value })
  commit('setProductsPage', 1)
  dispatch('getProducts')
}

export default {
  addTaggedFeatureToSelectedProduct,
  createFeature,
  getClassifications,
  getFeatures,
  getProducts,
  removeFeature,
  removeTaggedFeatureFromProduct,
  saveTaggedFeature,
  setOnlyMissing
}